import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Modal,
  TextInput,
  Alert,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import ListViewTable from '../components/ListViewTable';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { hasSuperUserAccess, normalizeStudentUserRecord } from '../utils/sessionUser';

// Object types will be loaded dynamically from the database

const RECORDS_PER_PAGE = 20;
const MAX_FIELDS_TO_DISPLAY = 15;
/** Matched height for object + list view trigger rows */
const RECORDS_DROPDOWN_TRIGGER_MIN_HEIGHT = 56;

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a ?? '') === String(b ?? '');
}

export default function RecordsPage({ navigation, route }) {
  const theme = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);
  const [objectTypes, setObjectTypes] = useState([]);
  const [selectedObjectType, setSelectedObjectType] = useState(null);
  const [showObjectDropdown, setShowObjectDropdown] = useState(false);
  const [listViews, setListViews] = useState([]);
  const [selectedListViewId, setSelectedListViewId] = useState(null);
  const [showListViewDropdown, setShowListViewDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingObjects, setLoadingObjects] = useState(true);
  const [objectsError, setObjectsError] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [settingsModalType, setSettingsModalType] = useState(null); // 'columns', 'filters', 'create', 'rename'
  const [listViewName, setListViewName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [deletingRecords, setDeletingRecords] = useState(false);
  const [isEditingRecords, setIsEditingRecords] = useState(false);
  const [savingRecords, setSavingRecords] = useState(false);
  const [objectFieldMeta, setObjectFieldMeta] = useState([]);
  const [pageRecords, setPageRecords] = useState([]);
  const [editBaseline, setEditBaseline] = useState({});
  const [draftEdits, setDraftEdits] = useState({});
  const [recordsSearchQuery, setRecordsSearchQuery] = useState('');

  useEffect(() => {
    if (!selectedObjectType) {
      setObjectFieldMeta([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${getApiUrl()}/api/objects/${encodeURIComponent(String(selectedObjectType))}/fields`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) {
          setObjectFieldMeta(data);
        }
      } catch {
        if (!cancelled) setObjectFieldMeta([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedObjectType]);

  useEffect(() => {
    setIsEditingRecords(false);
    setEditBaseline({});
    setDraftEdits({});
  }, [selectedRecordIds, selectedObjectType, selectedListViewId]);

  useEffect(() => {
    setRecordsSearchQuery('');
  }, [selectedObjectType, selectedListViewId]);

  const fieldMetaByName = useMemo(() => {
    const map = {};
    for (const field of objectFieldMeta) {
      if (field?.field_name) map[field.field_name] = field;
    }
    return map;
  }, [objectFieldMeta]);

  const hasDraftChanges = useMemo(() => {
    for (const rid of Object.keys(editBaseline)) {
      const base = editBaseline[rid];
      const draft = draftEdits[rid];
      if (!base || !draft) continue;
      for (const fieldName of Object.keys(fieldMetaByName)) {
        const meta = fieldMetaByName[fieldName];
        if (!meta?.is_editable) continue;
        if (!valuesEqual(base[fieldName], draft[fieldName])) return true;
      }
    }
    return false;
  }, [editBaseline, draftEdits, fieldMetaByName]);

  const resetEditState = useCallback(() => {
    setIsEditingRecords(false);
    setEditBaseline({});
    setDraftEdits({});
  }, []);

  const handlePageRecordsChange = useCallback((records) => {
    setPageRecords(Array.isArray(records) ? records : []);
  }, []);

  const handleDraftValueChange = useCallback((recordId, fieldName, value) => {
    const rid = String(recordId);
    setDraftEdits((prev) => ({
      ...prev,
      [rid]: {
        ...(prev[rid] || {}),
        [fieldName]: value,
      },
    }));
  }, []);

  const handleStartEdit = () => {
    if (!selectedObjectType || selectedRecordIds.length === 0) return;
    const selected = new Set(selectedRecordIds.map((id) => String(id)));
    const baseline = {};
    const drafts = {};
    pageRecords.forEach((row) => {
      const rid = String(row.id);
      if (!selected.has(rid)) return;
      baseline[rid] = { ...row };
      drafts[rid] = { ...row };
    });
    if (Object.keys(baseline).length === 0) {
      Alert.alert(
        'Edit records',
        'Selected records are not on this page. Go to the page that contains them, or select rows visible in the table.'
      );
      return;
    }
    setEditBaseline(baseline);
    setDraftEdits(drafts);
    setIsEditingRecords(true);
  };

  const handleSaveEdits = async () => {
    if (!hasSuperUserAccess(currentUser) || !selectedObjectType || !hasDraftChanges) return;
    const updates = [];
    for (const rid of Object.keys(editBaseline)) {
      const base = editBaseline[rid];
      const draft = draftEdits[rid];
      if (!base || !draft) continue;
      const fields = {};
      for (const [fieldName, meta] of Object.entries(fieldMetaByName)) {
        if (!meta?.is_editable) continue;
        if (!valuesEqual(base[fieldName], draft[fieldName])) {
          fields[fieldName] = draft[fieldName];
        }
      }
      if (Object.keys(fields).length > 0) {
        updates.push({ id: parseInt(rid, 10), fields });
      }
    }
    if (updates.length === 0) return;

    setSavingRecords(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/objects/${encodeURIComponent(String(selectedObjectType))}/records`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requester_id: currentUser.id,
            updates,
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Save failed (${res.status})`);
      }
      const count = body.count ?? body.updatedIds?.length ?? updates.length;
      resetEditState();
      setSelectedRecordIds([]);
      setRefreshKey((k) => k + 1);
      Alert.alert('Saved', `${count} record${count === 1 ? '' : 's'} updated.`);
    } catch (e) {
      Alert.alert('Save failed', e.message || 'Could not save record changes.');
    } finally {
      setSavingRecords(false);
    }
  };

  useEffect(() => {
    // Use user from route params if available, otherwise fetch
    if (route?.params?.user) {
      setCurrentUser(route.params.user);
      if (!hasSuperUserAccess(route.params.user)) {
        navigation.navigate('MainPage', { user: route.params.user });
      }
    } else {
      const userId = route?.params?.userId || 1;
      fetchUser(userId);
    }
  }, [route]);

  useEffect(() => {
    // Fetch all available objects from database
    fetchObjectTypes();
  }, []);

  const fetchObjectTypes = async () => {
    setLoadingObjects(true);
    setObjectsError(null);
    try {
      const apiUrl = `${getApiUrl()}/api/objects`;
      console.log('Fetching objects from:', apiUrl);
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Received objects data:', data);
        
        if (!Array.isArray(data)) {
          console.error('Expected array but got:', typeof data, data);
          setObjectTypes([]);
          setObjectsError('Invalid response format from server');
          return;
        }
        
        const formattedObjects = data.map(obj => ({
          value: obj.api_name,
          label: obj.label
        }));
        console.log('Formatted objects:', formattedObjects);
        setObjectTypes(formattedObjects);
        
        // Set first object as default if none selected
        if (formattedObjects.length > 0 && !selectedObjectType) {
          setSelectedObjectType(formattedObjects[0].value);
        }
      } else {
        const errorText = await response.text();
        console.error('Error fetching objects:', response.status, errorText);
        setObjectTypes([]);
        setObjectsError(`Failed to load objects: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching objects:', error);
      setObjectTypes([]);
      setObjectsError(`Network error: ${error.message}`);
    } finally {
      setLoadingObjects(false);
    }
  };

  useEffect(() => {
    if (hasSuperUserAccess(currentUser) && selectedObjectType) {
      console.log('Fetching list views for object:', selectedObjectType);
      fetchListViews();
    }
  }, [selectedObjectType, currentUser]);

  // Initial load: fetch list views when component mounts with initial object
  useEffect(() => {
    if (hasSuperUserAccess(currentUser) && selectedObjectType && listViews.length === 0 && !loading) {
      console.log('Initial load: fetching list views');
      fetchListViews();
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedListViewId) {
      // ListViewTable will handle fetching records
    }
  }, [selectedListViewId]);

  useEffect(() => {
    setSelectedRecordIds([]);
  }, [selectedObjectType, selectedListViewId]);

  const fetchUser = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = normalizeStudentUserRecord(await response.json());
        setCurrentUser(data);
        if (!hasSuperUserAccess(data)) {
          // Redirect if not super user, passing user data
          navigation.navigate('MainPage', { user: data });
        }
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchListViews = async () => {
    if (!selectedObjectType) return;
    
    setLoading(true);
    setSelectedListViewId(null); // Reset selection
    try {
      const response = await fetch(
        `${getApiUrl()}/api/objects/${selectedObjectType}/list-views`
      );
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched list views for', selectedObjectType, ':', data);
        setListViews(data || []);
        
        // Select default list view or first one
        const defaultView = data.find(lv => lv.is_default) || data[0];
        if (defaultView) {
          console.log('Setting selected list view:', defaultView.id, defaultView.name);
          setSelectedListViewId(defaultView.id);
        } else {
          console.warn('No list views found for', selectedObjectType);
        }
      } else {
        const errorText = await response.text();
        console.error('Error response:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error fetching list views:', error);
      setListViews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleObjectTypeSelect = (objectType) => {
    console.log('Object type selected:', objectType);
    setSelectedObjectType(objectType);
    setShowObjectDropdown(false);
    setSelectedListViewId(null); // Reset list view selection
    setListViews([]); // Clear previous list views
    setShowListViewDropdown(false); // Close list view dropdown
    // fetchListViews will be called by useEffect
  };

  const handleSettingsPress = () => {
    console.log('Settings button pressed', { selectedObjectType, selectedListViewId, showSettingsDropdown });
    if (!selectedObjectType || !selectedListViewId) {
      alert('Please select an object and list view first');
      return;
    }
    console.log('Toggling dropdown:', !showSettingsDropdown);
    setShowSettingsDropdown(!showSettingsDropdown);
  };

  const handleSelectFieldsToDisplay = async () => {
    setShowSettingsDropdown(false);
    setSettingsModalType('columns');
    setShowSettingsModal(true);
    setLoadingFields(true);

    try {
      // Fetch available fields for the object
      const fieldsResponse = await fetch(
        `${getApiUrl()}/api/objects/${selectedObjectType}/fields`
      );
      if (fieldsResponse.ok) {
        const fields = await fieldsResponse.json();
        setAvailableFields(fields);

        // Fetch current list view columns
        const listViewResponse = await fetch(
          `${getApiUrl()}/api/list-views/${selectedListViewId}`
        );
        if (listViewResponse.ok) {
          const listViewData = await listViewResponse.json();
          const currentColumns = listViewData.columns || [];

          // If no columns exist, use first 7 fields as default
          if (currentColumns.length === 0) {
            const defaultColumns = fields.slice(0, 7).map((field, index) => ({
              field_name: field.field_name,
              label_override: null,
              display_order: index,
              width: null,
            }));
            setSelectedColumns(defaultColumns);
          } else {
            // Use existing columns, maintaining order
            setSelectedColumns(
              currentColumns.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
            );
          }
        }
      }
    } catch (error) {
      console.error('Error loading fields:', error);
      alert('Error loading field information');
    } finally {
      setLoadingFields(false);
    }
  };

  const handleFiltersPress = async () => {
    setShowSettingsDropdown(false);
    setSettingsModalType('filters');
    setShowSettingsModal(true);
    setLoadingFields(true);

    try {
      // Fetch available fields for the object
      const fieldsResponse = await fetch(
        `${getApiUrl()}/api/objects/${selectedObjectType}/fields`
      );
      if (fieldsResponse.ok) {
        const fields = await fieldsResponse.json();
        setAvailableFields(fields);

        // Fetch current list view filters
        const listViewResponse = await fetch(
          `${getApiUrl()}/api/list-views/${selectedListViewId}`
        );
        if (listViewResponse.ok) {
          const listViewData = await listViewResponse.json();
          const currentFilters = listViewData.filters || [];
          setSelectedFilters(
            currentFilters.sort((a, b) => (a.filter_order || 0) - (b.filter_order || 0))
          );
        }
      }
    } catch (error) {
      console.error('Error loading filters:', error);
      alert('Error loading filter information');
    } finally {
      setLoadingFields(false);
    }
  };

  const handleCreateNewListViewPress = () => {
    setShowSettingsDropdown(false);
    setSettingsModalType('create');
    setShowSettingsModal(true);
    setListViewName('');
    setIsRenaming(false);
  };

  const handleRenameListViewPress = async () => {
    setShowSettingsDropdown(false);
    if (!selectedListViewId) {
      alert('Please select a list view first');
      return;
    }

    try {
      // Fetch current list view name
      const response = await fetch(`${getApiUrl()}/api/list-views/${selectedListViewId}`);
      if (response.ok) {
        const listViewData = await response.json();
        setListViewName(listViewData.name);
        setSettingsModalType('rename');
        setShowSettingsModal(true);
        setIsRenaming(true);
      } else {
        alert('Error loading list view');
      }
    } catch (error) {
      console.error('Error loading list view:', error);
      alert('Error loading list view');
    }
  };

  const handleCloneListViewPress = async () => {
    setShowSettingsDropdown(false);
    if (!selectedListViewId) {
      alert('Please select a list view first');
      return;
    }

    try {
      // Fetch current list view name to suggest a clone name
      const response = await fetch(`${getApiUrl()}/api/list-views/${selectedListViewId}`);
      if (response.ok) {
        const listViewData = await response.json();
        setListViewName(`${listViewData.name} (Copy)`);
        setSettingsModalType('clone');
        setShowSettingsModal(true);
        setIsRenaming(true);
      } else {
        alert('Error loading list view');
      }
    } catch (error) {
      console.error('Error loading list view:', error);
      alert('Error loading list view');
    }
  };

  const handleSaveRename = async () => {
    if (!listViewName.trim()) {
      alert('Please enter a name');
      return;
    }

    try {
      const response = await fetch(
        `${getApiUrl()}/api/list-views/${selectedListViewId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: listViewName.trim() }),
        }
      );

      if (response.ok) {
        alert('List view renamed successfully');
        setShowSettingsModal(false);
        setSettingsModalType(null);
        setListViewName('');
        setIsRenaming(false);
        // Refresh list views to show updated name
        await fetchListViews();
      } else {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Unknown error';
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || 'Unknown error';
        } else {
          const text = await response.text();
          errorMessage = text || 'Unknown error';
        }
        alert(`Error renaming list view: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error renaming list view:', error);
      alert('Error renaming list view');
    }
  };

  const handleSaveClone = async () => {
    if (!listViewName.trim()) {
      alert('Please enter a name');
      return;
    }

    try {
      const response = await fetch(
        `${getApiUrl()}/api/list-views/${selectedListViewId}/clone`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: listViewName.trim() }),
        }
      );

      if (response.ok) {
        const newListView = await response.json();
        // Refresh list views and select the new cloned view
        await fetchListViews();
        // Select the newly cloned list view
        setSelectedListViewId(newListView.id);
        // Keep the modal open and switch to rename mode
        setListViewName(newListView.name);
        setSettingsModalType('rename');
        setIsRenaming(true);
      } else {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Unknown error';
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || 'Unknown error';
        } else {
          const text = await response.text();
          errorMessage = text || 'Unknown error';
        }
        alert(`Error cloning list view: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error cloning list view:', error);
      alert('Error cloning list view');
    }
  };

  const handleSaveColumns = async () => {
    if (selectedColumns.length === 0) {
      alert('Please select at least one column');
      return;
    }

    try {
      const response = await fetch(
        `${getApiUrl()}/api/list-views/${selectedListViewId}/columns`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ columns: selectedColumns }),
        }
      );

      if (response.ok) {
        alert('Columns saved successfully');
        setShowSettingsModal(false);
        setSelectedColumns([]);
        setAvailableFields([]);
        // Refresh the list view to show updated columns
        const currentId = selectedListViewId;
        setSelectedListViewId(null);
        setTimeout(() => setSelectedListViewId(currentId), 100);
      } else {
        const errorData = await response.json();
        alert(`Error saving columns: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving columns:', error);
      alert('Error saving columns');
    }
  };

  const handleToggleColumn = (fieldName) => {
    const isSelected = selectedColumns.some(col => col.field_name === fieldName);
    
    if (isSelected) {
      // Remove column and reorder
      const newColumns = selectedColumns
        .filter(col => col.field_name !== fieldName)
        .map((col, index) => ({ ...col, display_order: index }));
      setSelectedColumns(newColumns);
    } else {
      // Add column at the end
      const field = availableFields.find(f => f.field_name === fieldName);
      if (field) {
        setSelectedColumns([
          ...selectedColumns,
          {
            field_name: field.field_name,
            label_override: null,
            display_order: selectedColumns.length,
            width: null,
          },
        ]);
      }
    }
  };

  const handleMoveColumn = (index, direction) => {
    const newColumns = [...selectedColumns];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < newColumns.length) {
      // Swap columns
      [newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]];
      // Update display_order
      newColumns.forEach((col, idx) => {
        col.display_order = idx;
      });
      setSelectedColumns(newColumns);
    }
  };

  const handleRecordPress = (record) => {
    if (!selectedObjectType || record?.id == null) return;
    const objectLabel = objectTypes.find((o) => o.value === selectedObjectType)?.label;
    navigation.navigate('RecordDetail', {
      objectApiName: selectedObjectType,
      recordId: record.id,
      objectLabel,
      user: currentUser,
      userId: currentUser?.id || route?.params?.userId || 1,
    });
  };

  const handleSaveFilters = async () => {
    try {
      const response = await fetch(
        `${getApiUrl()}/api/list-views/${selectedListViewId}/filters`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ filters: selectedFilters }),
        }
      );

      if (response.ok) {
        alert('Filters saved successfully');
        setShowSettingsModal(false);
        setSettingsModalType(null);
        setSelectedFilters([]);
        setAvailableFields([]);
        // Refresh the list view to show filtered results
        const currentId = selectedListViewId;
        setSelectedListViewId(null);
        setTimeout(() => setSelectedListViewId(currentId), 100);
      } else {
        const errorData = await response.json();
        alert(`Error saving filters: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving filters:', error);
      alert('Error saving filters');
    }
  };

  const handleCreateListView = async () => {
    if (!listViewName.trim()) {
      alert('Please enter a name for the list view');
      return;
    }

    try {
      const response = await fetch(
        `${getApiUrl()}/api/list-views`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            object_api_name: selectedObjectType,
            name: listViewName.trim(),
            is_public: true,
            is_default: false,
          }),
        }
      );

      if (response.ok) {
        const newListView = await response.json();
        alert('List view created successfully');
        setShowSettingsModal(false);
        setSettingsModalType(null);
        setListViewName('');
        
        // Refresh list views and select the new one
        await fetchListViews();
        setTimeout(() => {
          setSelectedListViewId(newListView.id);
        }, 100);
      } else {
        let errorMessage = `HTTP ${response.status}: `;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage += errorData.error || errorData.details || 'Unknown error';
            console.error('Error creating list view:', errorData);
          } catch (e) {
            errorMessage += 'Failed to parse error response';
            console.error('Error parsing JSON response:', e);
          }
        } else {
          try {
            const text = await response.text();
            errorMessage += text || 'Unknown error';
            console.error('Error creating list view (non-JSON response):', text);
          } catch (e) {
            errorMessage += 'Failed to read error response';
            console.error('Error reading response:', e);
          }
        }
        alert(`Error creating list view: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error creating list view:', error);
      alert(`Error creating list view: ${error.message || 'Unknown error'}`);
    }
  };

  const handleAddFilter = () => {
    const newFilter = {
      field_name: '',
      operator: '=',
      value: '',
      value_type: 'string',
      logical_group: 'AND',
      filter_order: selectedFilters.length,
    };
    setSelectedFilters([...selectedFilters, newFilter]);
  };

  const handleRemoveFilter = (index) => {
    const newFilters = selectedFilters.filter((_, i) => i !== index);
    // Reorder filters
    newFilters.forEach((filter, i) => {
      filter.filter_order = i;
    });
    setSelectedFilters(newFilters);
  };

  const handleUpdateFilter = (index, updates) => {
    const newFilters = [...selectedFilters];
    newFilters[index] = { ...newFilters[index], ...updates };
    setSelectedFilters(newFilters);
  };

  const confirmDeleteSelectedRecords = async () => {
    const n = selectedRecordIds.length;
    const objectLabel =
      objectTypes.find((ot) => ot.value === selectedObjectType)?.label || selectedObjectType;
    const msg = `Delete ${n} record${n === 1 ? '' : 's'} from ${objectLabel}? This cannot be undone.`;
    if (Platform.OS === 'web') {
      return window.confirm(msg);
    }
    return new Promise((resolve) => {
      Alert.alert('Delete records', msg, [
        { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
        { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
      ]);
    });
  };

  const handleDeleteSelectedRecords = async () => {
    if (!hasSuperUserAccess(currentUser) || !selectedObjectType || selectedRecordIds.length === 0) {
      return;
    }
    const confirmed = await confirmDeleteSelectedRecords();
    if (!confirmed) return;

    setDeletingRecords(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/objects/${encodeURIComponent(String(selectedObjectType))}/records`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requester_id: currentUser.id,
            ids: selectedRecordIds.map((id) => parseInt(String(id), 10)),
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Delete failed (${res.status})`);
      }
      const deleted = body.count ?? body.deletedIds?.length ?? 0;
      resetEditState();
      setSelectedRecordIds([]);
      setRefreshKey((k) => k + 1);
      Alert.alert('Deleted', `${deleted} record${deleted === 1 ? '' : 's'} removed.`);
    } catch (e) {
      Alert.alert('Delete failed', e.message || 'Could not delete records.');
    } finally {
      setDeletingRecords(false);
    }
  };

  const styles = getStyles(theme);

  if (!hasSuperUserAccess(currentUser)) {
    return null; // Or show access denied message
  }

  return (
    <View
      style={styles.container}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'records' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Records</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      {/* Toolbar: icons above, then object | list view dropdowns (equal row height) */}
      <View style={styles.recordsToolbar}>
        {showObjectDropdown && Platform.OS === 'web' && (
          <TouchableOpacity
            style={styles.recordsToolbarDismissOverlay}
            activeOpacity={1}
            onPress={() => setShowObjectDropdown(false)}
          />
        )}
        <View style={styles.toolbarIconsAbove}>
          <View style={styles.recordsSearchWrap}>
            <TextInput
              style={styles.recordsSearchInput}
              value={recordsSearchQuery}
              onChangeText={setRecordsSearchQuery}
              placeholder="Search…"
              placeholderTextColor={
                theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)'
              }
              autoCapitalize="none"
              autoCorrect={false}
              clearButtonMode="while-editing"
              {...(Platform.OS === 'web'
                ? { dataSet: { recordsSearchInput: 'true' } }
                : {})}
            />
            {recordsSearchQuery.length === 0 ? (
              <Text
                style={styles.recordsSearchIcon}
                {...(Platform.OS === 'web' ? { dataSet: { recordsSearchIcon: 'true' } } : {})}
              >
                🔍
              </Text>
            ) : null}
          </View>
          <View style={styles.toolbarIconRow}>
            <TouchableOpacity
              style={styles.refreshButton}
              onPress={() => {
                setRefreshKey((prev) => prev + 1);
              }}
              disabled={!selectedObjectType || !selectedListViewId}
            >
              <Text style={styles.refreshIcon}>🔄</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.settingsButton}
              onPress={handleSettingsPress}
              disabled={!selectedObjectType || !selectedListViewId}
            >
              <Text style={styles.settingsIcon}>⚙️</Text>
            </TouchableOpacity>
          </View>
        </View>
        <View style={styles.toolbarRow}>
          <View style={styles.objectDropdownColumn}>
            <View style={styles.dropdownWrapper}>
              <TouchableOpacity
                style={styles.dropdownButton}
                onPress={() => setShowObjectDropdown(!showObjectDropdown)}
                {...(Platform.OS === 'web' ? { dataSet: { recordsDropdownTrigger: 'true' } } : {})}
              >
                <Text style={styles.dropdownButtonText}>
                  {objectTypes.find(ot => ot.value === selectedObjectType)?.label || 'Select Object'}
                </Text>
                <Text style={styles.dropdownArrow}>{showObjectDropdown ? '▲' : '▼'}</Text>
              </TouchableOpacity>

              {showObjectDropdown && (
                <View
                  style={styles.dropdownMenu}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  <ScrollView
                    style={styles.dropdownScrollView}
                    contentContainerStyle={styles.dropdownScrollContent}
                    nestedScrollEnabled={true}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={true}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { recordsObjectDropdownScroll: 'true' } }
                      : {})}
                  >
                    {loadingObjects ? (
                      <View style={styles.dropdownOption}>
                        <Text style={styles.dropdownOptionText}>Loading...</Text>
                      </View>
                    ) : objectsError ? (
                      <View style={styles.dropdownOption}>
                        <Text style={[styles.dropdownOptionText, styles.errorText]}>
                          {objectsError}
                        </Text>
                      </View>
                    ) : objectTypes.length === 0 ? (
                      <View style={styles.dropdownOption}>
                        <Text style={styles.dropdownOptionText}>No objects available</Text>
                      </View>
                    ) : (
                      objectTypes.map((objectType) => (
                        <TouchableOpacity
                          key={objectType.value}
                          style={[
                            styles.dropdownOption,
                            selectedObjectType === objectType.value && styles.dropdownOptionSelected,
                          ]}
                          onPress={() => handleObjectTypeSelect(objectType.value)}
                          {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                        >
                          <Text
                            style={[
                              styles.dropdownOptionText,
                              selectedObjectType === objectType.value &&
                                styles.dropdownOptionTextSelected,
                            ]}
                          >
                            {objectType.label}
                          </Text>
                        </TouchableOpacity>
                      ))
                    )}
                  </ScrollView>
                </View>
              )}
            </View>
          </View>

          <View style={styles.listViewDropdownColumn}>
            <View style={styles.listViewDropdownHost}>
              {selectedObjectType && listViews.length > 0 && !loading ? (
                <View style={styles.listViewDropdownWrapper}>
                  <TouchableOpacity
                    style={styles.listViewDropdownButton}
                    onPress={() => setShowListViewDropdown(!showListViewDropdown)}
                    {...(Platform.OS === 'web' ? { dataSet: { recordsDropdownTrigger: 'true' } } : {})}
                  >
                    <Text style={styles.listViewDropdownButtonText}>
                      {listViews.find((lv) => lv.id === selectedListViewId)?.name || 'Select List View'}
                    </Text>
                    <Text style={styles.dropdownArrow}>{showListViewDropdown ? '▲' : '▼'}</Text>
                  </TouchableOpacity>

                  {showListViewDropdown && (
                    <View
                      style={styles.listViewDropdownMenu}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                    >
                      <ScrollView
                        style={styles.dropdownScrollView}
                        contentContainerStyle={styles.dropdownScrollContent}
                        nestedScrollEnabled={true}
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator={true}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { recordsListviewDropdownScroll: 'true' } }
                          : {})}
                      >
                        {listViews.map((lv) => (
                          <TouchableOpacity
                            key={lv.id}
                            style={[
                              styles.dropdownOption,
                              selectedListViewId === lv.id && styles.dropdownOptionSelected,
                            ]}
                            onPress={() => {
                              setSelectedListViewId(lv.id);
                              setShowListViewDropdown(false);
                            }}
                            {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                selectedListViewId === lv.id && styles.dropdownOptionTextSelected,
                              ]}
                            >
                              {lv.name}
                              {lv.is_default && ' (Default)'}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </View>
              ) : (
                <View style={styles.listViewPlaceholder}>
                  <Text style={styles.listViewPlaceholderText} numberOfLines={1}>
                    {loading ? 'Loading views…' : 'List view'}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </View>
      
      {/* Dropdown Menu - Rendered outside wrapper to avoid clipping */}
      {showSettingsDropdown && selectedObjectType && selectedListViewId && (
        <>
          {Platform.OS === 'web' && (
            <TouchableOpacity
              style={styles.dropdownOverlay}
              activeOpacity={1}
              onPress={() => setShowSettingsDropdown(false)}
            />
          )}
          <View style={styles.settingsDropdownMenu}>
            <TouchableOpacity
              style={styles.settingsDropdownOption}
              onPress={handleSelectFieldsToDisplay}
            >
              <Text style={styles.settingsDropdownOptionText}>Select fields to display</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.settingsDropdownOption}
              onPress={handleFiltersPress}
            >
              <Text style={styles.settingsDropdownOptionText}>Filters</Text>
            </TouchableOpacity>
            {selectedListViewId && (
              <>
                <TouchableOpacity
                  style={styles.settingsDropdownOption}
                  onPress={handleRenameListViewPress}
                >
                  <Text style={styles.settingsDropdownOptionText}>Rename list view</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.settingsDropdownOption}
                  onPress={handleCloneListViewPress}
                >
                  <Text style={styles.settingsDropdownOptionText}>Clone</Text>
                </TouchableOpacity>
              </>
            )}
            <TouchableOpacity
              style={styles.settingsDropdownOption}
              onPress={handleCreateNewListViewPress}
            >
              <Text style={styles.settingsDropdownOptionText}>Create new list view</Text>
            </TouchableOpacity>
          </View>
        </>
      )}

      {hasSuperUserAccess(currentUser) && selectedRecordIds.length > 0 ? (
        <View
          style={[
            styles.bulkActionBar,
            {
              borderColor: theme.colors.border,
              backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F3F2F2',
            },
          ]}
        >
          <Text style={[styles.bulkActionCount, { color: theme.colors.text }]}>
            {selectedRecordIds.length} selected
          </Text>
          <View style={styles.bulkActionBtnColumn}>
            <TouchableOpacity
              style={[styles.bulkActionBtn, styles.bulkActionBtnDanger]}
              onPress={handleDeleteSelectedRecords}
              disabled={deletingRecords || savingRecords}
            >
              <Text style={styles.bulkActionBtnDangerText}>
                {deletingRecords ? 'Deleting…' : 'Delete'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.bulkActionBtn,
                styles.bulkActionBtnEdit,
                isEditingRecords && styles.bulkActionBtnEditActive,
              ]}
              onPress={handleStartEdit}
              disabled={deletingRecords || savingRecords || isEditingRecords}
            >
              <Text
                style={[
                  styles.bulkActionBtnEditText,
                  isEditingRecords && styles.bulkActionBtnEditTextActive,
                ]}
              >
                {isEditingRecords ? 'Editing' : 'Edit'}
              </Text>
            </TouchableOpacity>
            {isEditingRecords && hasDraftChanges ? (
              <TouchableOpacity
                style={[styles.bulkActionBtn, styles.bulkActionBtnSave]}
                onPress={handleSaveEdits}
                disabled={savingRecords || deletingRecords}
              >
                <Text style={styles.bulkActionBtnSaveText}>
                  {savingRecords ? 'Saving…' : 'Save'}
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
          <TouchableOpacity
            style={[styles.bulkActionBtn, { borderColor: theme.colors.border }]}
            onPress={() => {
              resetEditState();
              setSelectedRecordIds([]);
            }}
            disabled={deletingRecords || savingRecords}
          >
            <Text style={[styles.bulkActionBtnText, { color: theme.colors.text }]}>
              Clear selection
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {/* Records List - Metadata-driven table */}
      <View style={styles.listContainer}>
        {selectedListViewId ? (
          <ListViewTable
            listViewId={selectedListViewId}
            onRecordPress={handleRecordPress}
            recordsPerPage={RECORDS_PER_PAGE}
            refreshKey={refreshKey}
            enableBulkSelect={!!hasSuperUserAccess(currentUser)}
            selectedRecordIds={selectedRecordIds}
            onSelectedRecordIdsChange={setSelectedRecordIds}
            editMode={isEditingRecords}
            editableRecordIds={isEditingRecords ? selectedRecordIds : []}
            fieldMetaByName={fieldMetaByName}
            draftEdits={draftEdits}
            onDraftValueChange={handleDraftValueChange}
            onPageRecordsChange={handlePageRecordsChange}
            searchQuery={recordsSearchQuery}
          />
        ) : loading ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>Select an object to view records</Text>
          </View>
        )}
      </View>

      {/* Settings Modal - Column Selection / Filters / Create New */}
      <Modal
        visible={showSettingsModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => {
          setShowSettingsModal(false);
          setSettingsModalType(null);
          setSelectedColumns([]);
          setSelectedFilters([]);
          setAvailableFields([]);
          setListViewName('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContent,
              settingsModalType === 'filters' && styles.modalContentWide,
              settingsModalType === 'columns' && styles.modalContentColumns,
            ]}
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    ...(settingsModalType === 'filters'
                      ? { recordsFiltersModal: 'true' }
                      : {}),
                    ...(settingsModalType === 'columns'
                      ? { recordsColumnsModal: 'true' }
                      : {}),
                  },
                }
              : {})}
          >
            {/* Column Selection Modal */}
            {settingsModalType === 'columns' && (
              <>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Select Columns</Text>
                  <TouchableOpacity
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setSelectedColumns([]);
                      setAvailableFields([]);
                    }}
                    style={styles.modalCloseButton}
                  >
                    <Text style={styles.modalCloseText}>✕</Text>
                  </TouchableOpacity>
                </View>
                
                <View style={styles.modalBody}>
                  {loadingFields ? (
                    <Text style={styles.loadingText}>Loading fields...</Text>
                  ) : (
                    <View style={styles.columnsModalBody}>
                      <View style={styles.columnsSection}>
                        <Text style={styles.modalLabel}>
                          Available Fields ({availableFields.length})
                        </Text>
                        <ScrollView
                          style={styles.fieldsList}
                          contentContainerStyle={styles.columnsListContent}
                          nestedScrollEnabled={true}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { recordsColumnsList: 'available' } }
                            : {})}
                        >
                          {availableFields.map((field) => {
                            const isSelected = selectedColumns.some(
                              col => col.field_name === field.field_name
                            );
                            return (
                              <TouchableOpacity
                                key={field.field_name}
                                style={[
                                  styles.fieldItem,
                                  isSelected && styles.fieldItemSelected,
                                ]}
                                onPress={() => handleToggleColumn(field.field_name)}
                              >
                                <View style={styles.fieldCheckbox}>
                                  {isSelected && <Text style={styles.checkmark}>✓</Text>}
                                </View>
                                <Text style={styles.fieldName}>{field.label}</Text>
                                <Text style={styles.fieldType}>({field.data_type})</Text>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      </View>

                      <View style={styles.columnsSection}>
                        <Text style={styles.modalLabel}>
                          Selected Columns ({selectedColumns.length})
                        </Text>
                        <ScrollView
                          style={styles.selectedColumnsList}
                          contentContainerStyle={styles.columnsListContent}
                          nestedScrollEnabled={true}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { recordsColumnsList: 'selected' } }
                            : {})}
                        >
                          {selectedColumns.length === 0 ? (
                            <Text style={styles.emptyText}>No columns selected</Text>
                          ) : (
                            selectedColumns.map((col, index) => {
                              const field = availableFields.find(f => f.field_name === col.field_name);
                              return (
                                <View key={col.field_name} style={styles.selectedColumnItem}>
                                  <View style={styles.columnOrderControls}>
                                    <TouchableOpacity
                                      style={[styles.orderButton, index === 0 && styles.orderButtonDisabled]}
                                      onPress={() => handleMoveColumn(index, 'up')}
                                      disabled={index === 0}
                                    >
                                      <Text style={styles.orderButtonText}>↑</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                      style={[
                                        styles.orderButton,
                                        index === selectedColumns.length - 1 && styles.orderButtonDisabled,
                                      ]}
                                      onPress={() => handleMoveColumn(index, 'down')}
                                      disabled={index === selectedColumns.length - 1}
                                    >
                                      <Text style={styles.orderButtonText}>↓</Text>
                                    </TouchableOpacity>
                                  </View>
                                  <Text style={styles.selectedColumnName}>
                                    {index + 1}. {field?.label || col.field_name}
                                  </Text>
                                  <TouchableOpacity
                                    style={styles.removeButton}
                                    onPress={() => handleToggleColumn(col.field_name)}
                                  >
                                    <Text style={styles.removeButtonText}>✕</Text>
                                  </TouchableOpacity>
                                </View>
                              );
                            })
                          )}
                        </ScrollView>
                      </View>
                    </View>
                  )}
                </View>
                
                <View style={styles.modalFooter}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalButtonCancel]}
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setSelectedColumns([]);
                      setAvailableFields([]);
                    }}
                  >
                    <Text style={styles.modalButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.modalButton,
                      styles.modalButtonSave,
                      (loadingFields || selectedColumns.length === 0) && styles.modalButtonDisabled,
                    ]}
                    onPress={handleSaveColumns}
                    disabled={loadingFields || selectedColumns.length === 0}
                  >
                    <Text style={styles.modalButtonText}>Save Columns</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {/* Filters Modal */}
            {settingsModalType === 'filters' && (
              <>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Filters</Text>
                  <TouchableOpacity
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setSelectedFilters([]);
                      setAvailableFields([]);
                    }}
                    style={styles.modalCloseButton}
                  >
                    <Text style={styles.modalCloseText}>✕</Text>
                  </TouchableOpacity>
                </View>
                
                <View style={styles.modalBody}>
                  {loadingFields ? (
                    <Text style={styles.loadingText}>Loading fields...</Text>
                  ) : (
                    <>
                      <View style={styles.filtersHeader}>
                        <Text style={styles.modalLabel}>
                          Filter Conditions ({selectedFilters.length})
                        </Text>
                        <TouchableOpacity
                          style={styles.addFilterButton}
                          onPress={handleAddFilter}
                        >
                          <Text style={styles.addFilterButtonText}>+ Add Filter</Text>
                        </TouchableOpacity>
                      </View>
                      
                      <ScrollView style={styles.filtersList} nestedScrollEnabled={true}>
                        {selectedFilters.length === 0 ? (
                          <Text style={styles.emptyText}>No filters applied</Text>
                        ) : (
                          selectedFilters.map((filter, index) => (
                            <FilterRow
                              key={`filter-${index}-${filter.field_name || 'new'}`}
                              theme={theme}
                              filter={filter}
                              index={index}
                              availableFields={availableFields}
                              onUpdate={(updates) => handleUpdateFilter(index, updates)}
                              onRemove={() => handleRemoveFilter(index)}
                              isFirst={index === 0}
                            />
                          ))
                        )}
                      </ScrollView>
                    </>
                  )}
                </View>
                
                <View style={styles.modalFooter}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalButtonCancel]}
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setSelectedFilters([]);
                      setAvailableFields([]);
                    }}
                  >
                    <Text style={styles.modalButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalButtonSave]}
                    onPress={handleSaveFilters}
                    disabled={loadingFields}
                  >
                    <Text style={styles.modalButtonText}>Save Filters</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {/* Create New List View Modal */}
            {settingsModalType === 'create' && (
              <>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Create New List View</Text>
                  <TouchableOpacity
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setListViewName('');
                      setIsRenaming(false);
                    }}
                    style={styles.modalCloseButton}
                  >
                    <Text style={styles.modalCloseText}>✕</Text>
                  </TouchableOpacity>
                </View>
                
                <View style={styles.modalBody}>
                  <Text style={styles.modalLabel}>
                    List View Name
                  </Text>
                  <TextInput
                    style={styles.listViewNameInput}
                    value={listViewName}
                    onChangeText={setListViewName}
                    placeholder="Enter list view name"
                    placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                  />
                  <Text style={[styles.modalHelpText, { marginTop: 10 }]}>
                    This list view will be available for {objectTypes.find(ot => ot.value === selectedObjectType)?.label || 'this object'}.
                  </Text>
                </View>
                
                <View style={styles.modalFooter}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalButtonCancel]}
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setListViewName('');
                      setIsRenaming(false);
                    }}
                  >
                    <Text style={styles.modalButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.modalButton,
                      styles.modalButtonSave,
                      !listViewName.trim() && styles.modalButtonDisabled,
                    ]}
                    onPress={handleCreateListView}
                    disabled={!listViewName.trim()}
                  >
                    <Text style={styles.modalButtonText}>Create</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {/* Rename List View Modal */}
            {settingsModalType === 'rename' && (
              <>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Rename List View</Text>
                  <TouchableOpacity
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setListViewName('');
                      setIsRenaming(false);
                    }}
                    style={styles.modalCloseButton}
                  >
                    <Text style={styles.modalCloseText}>✕</Text>
                  </TouchableOpacity>
                </View>
                
                <View style={styles.modalBody}>
                  <Text style={styles.modalLabel}>
                    List View Name
                  </Text>
                  <TextInput
                    style={styles.listViewNameInput}
                    value={listViewName}
                    onChangeText={setListViewName}
                    placeholder="Enter list view name"
                    placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                    autoFocus={true}
                  />
                </View>
                
                <View style={styles.modalFooter}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalButtonCancel]}
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setListViewName('');
                      setIsRenaming(false);
                    }}
                  >
                    <Text style={styles.modalButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.modalButton,
                      styles.modalButtonSave,
                      !listViewName.trim() && styles.modalButtonDisabled,
                    ]}
                    onPress={handleSaveRename}
                    disabled={!listViewName.trim()}
                  >
                    <Text style={styles.modalButtonText}>Save</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {/* Clone List View Modal */}
            {settingsModalType === 'clone' && (
              <>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Clone List View</Text>
                  <TouchableOpacity
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setListViewName('');
                      setIsRenaming(false);
                    }}
                    style={styles.modalCloseButton}
                  >
                    <Text style={styles.modalCloseText}>✕</Text>
                  </TouchableOpacity>
                </View>
                
                <View style={styles.modalBody}>
                  <Text style={styles.modalLabel}>
                    New List View Name
                  </Text>
                  <TextInput
                    style={styles.listViewNameInput}
                    value={listViewName}
                    onChangeText={setListViewName}
                    placeholder="Enter list view name"
                    placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                    autoFocus={true}
                  />
                  <Text style={[styles.modalHelpText, { marginTop: 10 }]}>
                    This will create a copy of the current list view with the same columns, filters, and sorting.
                  </Text>
                </View>
                
                <View style={styles.modalFooter}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalButtonCancel]}
                    onPress={() => {
                      setShowSettingsModal(false);
                      setSettingsModalType(null);
                      setListViewName('');
                      setIsRenaming(false);
                    }}
                  >
                    <Text style={styles.modalButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.modalButton,
                      styles.modalButtonSave,
                      !listViewName.trim() && styles.modalButtonDisabled,
                    ]}
                    onPress={handleSaveClone}
                    disabled={!listViewName.trim()}
                  >
                    <Text style={styles.modalButtonText}>Clone</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

// Dropdown max height for scrolling
const DROPDOWN_MAX_HEIGHT_SCROLL = 350; // Max height when scrolling is enabled

const getStyles = (theme) => {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: 'transparent',
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: 'transparent',
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  recordsToolbar: {
    paddingHorizontal: 20,
    paddingBottom: 12,
    zIndex: 10000,
    elevation: 10000,
    ...(Platform.OS === 'web' ? { position: 'relative' } : {}),
  },
  toolbarIconsAbove: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 8,
    gap: 12,
  },
  recordsSearchWrap: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    justifyContent: 'center',
  },
  recordsSearchInput: {
    width: '100%',
    minHeight: 40,
    paddingLeft: 14,
    paddingRight: 40,
    paddingVertical: Platform.OS === 'web' ? 10 : 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    fontSize: 15,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  recordsSearchIcon: {
    position: 'absolute',
    right: 12,
    fontSize: 16,
    opacity: 0.55,
    pointerEvents: 'none',
    backgroundColor: 'transparent',
    color: theme.colors.text,
  },
  toolbarRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 12,
    zIndex: 1500,
    position: 'relative',
  },
  objectDropdownColumn: {
    flex: 1,
    minWidth: 0,
  },
  listViewDropdownColumn: {
    flex: 1,
    minWidth: 0,
  },
  listViewDropdownHost: {
    flex: 1,
    minWidth: 0,
    alignSelf: 'stretch',
  },
  toolbarIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  listViewPlaceholder: {
    flex: 1,
    justifyContent: 'center',
    minHeight: RECORDS_DROPDOWN_TRIGGER_MIN_HEIGHT,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: 'transparent',
    opacity: 0.75,
    alignSelf: 'stretch',
  },
  listViewPlaceholderText: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.7,
  },
  recordsToolbarDismissOverlay: {
    ...(Platform.OS === 'web'
      ? {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 800,
          backgroundColor: 'transparent',
        }
      : {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 800,
          backgroundColor: 'transparent',
        }),
  },
  dropdownWrapper: {
    width: '100%',
    position: 'relative',
    zIndex: 10001,
    flex: 1,
    alignSelf: 'stretch',
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    width: '100%',
    minWidth: 120,
    minHeight: RECORDS_DROPDOWN_TRIGGER_MIN_HEIGHT,
    flex: 1,
  },
  dropdownButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    flex: 1,
    marginRight: 8,
    flexShrink: 1,
    backgroundColor: 'transparent',
  },
  dropdownArrow: {
    fontSize: 12,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginTop: 5,
    minHeight: 50,
    maxHeight: DROPDOWN_MAX_HEIGHT_SCROLL,
    zIndex: 10002,
    elevation: 10002,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      position: 'absolute',
      pointerEvents: 'auto',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 10002,
    }),
  },
  dropdownScrollView: {
    flexGrow: 0,
    maxHeight: DROPDOWN_MAX_HEIGHT_SCROLL,
    ...(Platform.OS === 'web' && {
      overflowY: 'auto',
      overflowX: 'hidden',
    }),
  },
  dropdownScrollContent: {
    flexGrow: 0,
  },
  dropdownOption: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  dropdownOptionSelected: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownOptionTextSelected: {
    color: '#FFFFFF',
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  errorText: {
    color: '#FF4444',
    fontSize: 14,
  },
  refreshButton: {
    padding: 10,
    zIndex: 1001,
  },
  refreshIcon: {
    fontSize: 24,
  },
  settingsButton: {
    padding: 10,
    zIndex: 1001,
  },
  settingsIcon: {
    fontSize: 24,
  },
  dropdownOverlay: {
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
    } : {
      position: 'absolute',
    }),
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
    backgroundColor: 'transparent',
  },
  settingsDropdownMenu: {
    backgroundColor: '#1A1A1A',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    minWidth: 200,
    zIndex: 10000,
    overflow: 'visible',
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 'auto',
      bottom: 'auto',
      right: 20,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
    } : {
      position: 'absolute',
      top: '100%',
      right: 0,
      marginTop: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 10000,
    }),
  },
  settingsDropdownOption: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  settingsDropdownOptionText: {
    fontSize: 16,
    color: theme.colors.text,
  },
  bulkActionBar: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 10,
    marginHorizontal: 20,
    marginBottom: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderRadius: 6,
  },
  bulkActionBtnColumn: {
    flexDirection: 'column',
    gap: 8,
    backgroundColor: 'transparent',
  },
  bulkActionCount: {
    fontSize: 14,
    fontWeight: '600',
    marginRight: 8,
  },
  bulkActionBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
  },
  bulkActionBtnDanger: {
    backgroundColor: '#c23934',
    borderColor: '#a61d1a',
  },
  bulkActionBtnDangerText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
  bulkActionBtnEdit: {
    borderColor: '#d4af37',
    backgroundColor: 'transparent',
  },
  bulkActionBtnEditActive: {
    backgroundColor: 'rgba(212, 175, 55, 0.2)',
  },
  bulkActionBtnEditText: {
    color: '#d4af37',
    fontSize: 14,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
  bulkActionBtnEditTextActive: {
    color: '#000000',
  },
  bulkActionBtnSave: {
    backgroundColor: '#d4af37',
    borderColor: '#d4af37',
  },
  bulkActionBtnSaveText: {
    color: '#000000',
    fontSize: 14,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
  bulkActionBtnText: {
    fontSize: 14,
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  listContainer: {
    flex: 1,
    paddingHorizontal: 20,
    zIndex: 1,
    ...(Platform.OS === 'web' ? {
      position: 'relative',
    } : {}),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: theme.colors.text,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: theme.colors.text,
    opacity: 0.6,
    backgroundColor: 'transparent',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: theme.isDarkMode ? '#333' : '#F5F5F5',
    borderBottomWidth: 2,
    borderBottomColor: theme.colors.primary || '#d4af37',
  },
  headerCell: {
    padding: 12,
    minWidth: 120,
    borderRightWidth: 1,
    borderRightColor: theme.colors.border,
  },
  headerText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  tableScrollView: {
    flex: 1,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  tableCell: {
    padding: 12,
    minWidth: 120,
    borderRightWidth: 1,
    borderRightColor: theme.colors.border,
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F9F9F9',
  },
  paginationButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  paginationButtonDisabled: {
    backgroundColor: '#999',
    opacity: 0.5,
  },
  paginationButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  paginationText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  listViewDropdownWrapper: {
    width: '100%',
    position: 'relative',
    flex: 1,
    alignSelf: 'stretch',
  },
  listViewDropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    width: '100%',
    minWidth: 120,
    minHeight: RECORDS_DROPDOWN_TRIGGER_MIN_HEIGHT,
    flex: 1,
  },
  listViewDropdownButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    flex: 1,
    marginRight: 8,
    flexShrink: 1,
    backgroundColor: 'transparent',
  },
  listViewDropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginTop: 5,
    maxHeight: DROPDOWN_MAX_HEIGHT_SCROLL,
    zIndex: 10002,
    elevation: 10002,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    }),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '90%',
    maxWidth: 500,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 10,
    }),
  },
  modalContentWide: {
    maxWidth: 760,
    overflow: 'visible',
  },
  modalContentColumns: {
    maxWidth: 820,
    width: '95%',
  },
  columnsModalBody: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: Platform.OS === 'web' ? 16 : 12,
    alignItems: 'stretch',
    backgroundColor: 'transparent',
  },
  columnsSection: {
    flex: 1,
    minWidth: 0,
    backgroundColor: 'transparent',
  },
  columnsListContent: {
    paddingBottom: 4,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.heading,
  },
  modalCloseButton: {
    padding: 5,
  },
  modalCloseText: {
    fontSize: 24,
    color: theme.colors.text,
  },
  modalBody: {
    marginBottom: 20,
  },
  modalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  modalInput: {
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#F5F5F5',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  modalButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  modalButtonCancel: {
    backgroundColor: '#999',
  },
  modalButtonSave: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  modalButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  modalButtonDisabled: {
    opacity: 0.5,
  },
  fieldsList: {
    maxHeight: 280,
    minHeight: 160,
    marginBottom: 0,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 6,
    backgroundColor: theme.isDarkMode ? '#141414' : '#FAFAFA',
    ...(Platform.OS === 'web' ? { overflow: 'hidden' } : {}),
  },
  fieldItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginBottom: 8,
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F5F5F5',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  fieldItemSelected: {
    backgroundColor: theme.isDarkMode ? '#2a2a00' : '#FFF9C4',
    borderColor: theme.isDarkMode ? '#FDFD0A' : '#FFD700',
  },
  fieldCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: theme.isDarkMode ? '#FDFD0A' : '#0070D2',
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#000000' : '#FFFFFF',
  },
  checkmark: {
    color: theme.isDarkMode ? '#FDFD0A' : '#0070D2',
    fontSize: 16,
    fontWeight: 'bold',
  },
  fieldName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  fieldType: {
    fontSize: 12,
    color: theme.isDarkMode ? '#888888' : '#666666',
    marginLeft: 8,
    backgroundColor: 'transparent',
  },
  selectedColumnsList: {
    maxHeight: 280,
    minHeight: 160,
    marginBottom: 0,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 6,
    backgroundColor: theme.isDarkMode ? '#141414' : '#FAFAFA',
    ...(Platform.OS === 'web' ? { overflow: 'hidden' } : {}),
  },
  selectedColumnItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginBottom: 8,
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F5F5F5',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.isDarkMode ? '#FDFD0A' : '#0070D2',
  },
  columnOrderControls: {
    flexDirection: 'row',
    marginRight: 12,
  },
  orderButton: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
    borderRadius: 4,
    marginRight: 4,
  },
  orderButtonDisabled: {
    opacity: 0.3,
  },
  orderButtonText: {
    fontSize: 16,
    color: theme.colors.text,
    fontWeight: 'bold',
  },
  selectedColumnName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  removeButton: {
    width: 28,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
    borderRadius: 4,
  },
  removeButtonText: {
    fontSize: 16,
    color: theme.isDarkMode ? '#FF4444' : '#CC0000',
    fontWeight: 'bold',
  },
  filtersHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  addFilterButton: {
    paddingHorizontal: 15,
    paddingVertical: 8,
    backgroundColor: theme.isDarkMode ? '#FDFD0A' : '#0070D2',
    borderRadius: 4,
  },
  addFilterButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.isDarkMode ? '#000000' : '#FFFFFF',
  },
  filtersList: {
    maxHeight: 400,
    ...(Platform.OS === 'web' ? { overflow: 'visible' } : {}),
  },
  listViewNameInput: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 4,
    padding: 15,
    backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
    color: theme.colors.text,
    fontSize: 16,
    marginTop: 5,
  },
  modalHelpText: {
    fontSize: 12,
    color: theme.isDarkMode ? '#888' : '#666',
    fontStyle: 'italic',
  },
  });
};

// Filter dropdown — portal + fixed position on web (modal is outside data-screen="records")
function FilterSelectDropdown({
  styles,
  label,
  displayValue,
  open,
  onOpenChange,
  children,
}) {
  const triggerRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  const close = useCallback(() => {
    onOpenChange(false);
    setMenuRect(null);
  }, [onOpenChange]);

  const toggle = useCallback(() => {
    if (open) {
      close();
      return;
    }
    if (Platform.OS === 'web' && triggerRef.current?.measureInWindow) {
      triggerRef.current.measureInWindow((x, y, width, height) => {
        setMenuRect({
          top: y + height + 2,
          left: x,
          width: Math.max(width, 180),
        });
        onOpenChange(true);
      });
      return;
    }
    onOpenChange(true);
  }, [open, close, onOpenChange]);

  const webMenuReady = Platform.OS !== 'web' || menuRect != null;

  const menuNode =
    open && webMenuReady ? (
      <>
        {Platform.OS === 'web' ? (
          <TouchableOpacity
            style={styles.filterDropdownOverlay}
            activeOpacity={1}
            onPress={close}
            {...(Platform.OS === 'web'
              ? { dataSet: { recordsFilterDropdownOverlay: 'true' } }
              : {})}
          />
        ) : null}
        <View
          style={[
            styles.filterDropdownMenu,
            Platform.OS === 'web' && menuRect
              ? {
                  position: 'fixed',
                  top: menuRect.top,
                  left: menuRect.left,
                  width: menuRect.width,
                  right: undefined,
                }
              : null,
          ]}
          {...(Platform.OS === 'web'
            ? { dataSet: { recordsFilterDropdownMenu: 'true' } }
            : {})}
        >
          <ScrollView style={styles.filterDropdownScroll} nestedScrollEnabled>
            {children}
          </ScrollView>
        </View>
      </>
    ) : null;

  const portaledMenu =
    Platform.OS === 'web' &&
    menuNode &&
    typeof document !== 'undefined'
      ? require('react-dom').createPortal(
          <div
            data-records-filter-dropdown-portal="true"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100000,
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
              {menuNode}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <View style={[styles.filterField, open && styles.filterFieldOpen]}>
        <Text style={styles.filterLabel}>{label}</Text>
        <View ref={triggerRef} collapsable={false} style={styles.filterTriggerShell}>
          <TouchableOpacity
            style={styles.filterDropdownButton}
            onPress={toggle}
            {...(Platform.OS === 'web'
              ? { dataSet: { recordsFilterDropdownTrigger: 'true' } }
              : {})}
          >
            <Text style={styles.filterDropdownButtonText}>{displayValue}</Text>
            <Text style={styles.dropdownArrow}>{open ? '▲' : '▼'}</Text>
          </TouchableOpacity>
        </View>
        {Platform.OS !== 'web' ? menuNode : null}
      </View>
      {portaledMenu}
    </>
  );
}

// Filter Row Component
function FilterRow({ theme, filter, index, availableFields, onUpdate, onRemove, isFirst }) {
  const styles = getFilterRowStyles(theme);
  const operators = ['=', '!=', '>', '<', '>=', '<=', 'LIKE', 'IN', 'NOT IN', 'IS NULL', 'IS NOT NULL'];
  const [showFieldDropdown, setShowFieldDropdown] = React.useState(false);
  const [showOperatorDropdown, setShowOperatorDropdown] = React.useState(false);

  // Close dropdowns when filter changes externally
  React.useEffect(() => {
    setShowFieldDropdown(false);
    setShowOperatorDropdown(false);
  }, [filter.field_name, filter.operator]);

  const selectedField = availableFields.find(f => f.field_name === filter.field_name);
  const needsValue = !['IS NULL', 'IS NOT NULL'].includes(filter.operator);

  const handleFieldSelect = (field) => {
    // Auto-detect value type based on field data type
    let valueType = 'string';
    if (field.data_type.includes('int') || field.data_type.includes('numeric') || field.data_type.includes('decimal')) {
      valueType = 'number';
    } else if (field.data_type.includes('bool')) {
      valueType = 'boolean';
    } else if (field.data_type.includes('date') || field.data_type.includes('timestamp')) {
      valueType = 'date';
    }
    // Update both field_name and value_type in a single call
    onUpdate({ 
      field_name: field.field_name,
      value_type: valueType,
      value: '', // Clear value when field changes
    });
    setShowFieldDropdown(false);
  };

  const handleOperatorSelect = (op) => {
    onUpdate({ operator: op });
    setShowOperatorDropdown(false);
  };

  return (
    <View style={styles.filterRow}>
      {!isFirst && (
        <View style={styles.logicalGroupContainer}>
          <Text style={styles.logicalGroupLabel}>And</Text>
          <TouchableOpacity
            style={styles.logicalGroupButton}
            onPress={() => onUpdate({
              logical_group: filter.logical_group === 'AND' ? 'OR' : 'AND'
            })}
          >
            <Text style={styles.logicalGroupButtonText}>
              {filter.logical_group || 'AND'}
            </Text>
          </TouchableOpacity>
        </View>
      )}
      
      <View style={styles.filterFields}>
        <FilterSelectDropdown
          styles={styles}
          label="Field"
          displayValue={selectedField ? selectedField.label : 'Select field'}
          open={showFieldDropdown}
          onOpenChange={(next) => {
            setShowFieldDropdown(next);
            if (next) setShowOperatorDropdown(false);
          }}
        >
          {availableFields.map((field) => (
            <TouchableOpacity
              key={field.field_name}
              style={[
                styles.filterOption,
                filter.field_name === field.field_name && styles.filterOptionSelected,
              ]}
              onPress={() => handleFieldSelect(field)}
              {...(Platform.OS === 'web'
                ? { dataSet: { recordsFilterDropdownOption: 'true' } }
                : {})}
            >
              <Text
                style={[
                  styles.filterOptionText,
                  filter.field_name === field.field_name && styles.filterOptionTextSelected,
                ]}
              >
                {field.label}
              </Text>
            </TouchableOpacity>
          ))}
        </FilterSelectDropdown>

        <FilterSelectDropdown
          styles={styles}
          label="Operator"
          displayValue={filter.operator || '='}
          open={showOperatorDropdown}
          onOpenChange={(next) => {
            setShowOperatorDropdown(next);
            if (next) setShowFieldDropdown(false);
          }}
        >
          {operators.map((op) => (
            <TouchableOpacity
              key={op}
              style={[
                styles.filterOption,
                filter.operator === op && styles.filterOptionSelected,
              ]}
              onPress={() => handleOperatorSelect(op)}
              {...(Platform.OS === 'web'
                ? { dataSet: { recordsFilterDropdownOption: 'true' } }
                : {})}
            >
              <Text
                style={[
                  styles.filterOptionText,
                  filter.operator === op && styles.filterOptionTextSelected,
                ]}
              >
                {op}
              </Text>
            </TouchableOpacity>
          ))}
        </FilterSelectDropdown>

        {/* Value Input (if needed) */}
        {needsValue && (
          <View style={styles.filterField}>
            <Text style={styles.filterLabel}>Value</Text>
            <TextInput
              style={styles.filterValueInput}
              value={filter.value || ''}
              onChangeText={(value) => onUpdate({ value })}
              placeholder="Enter value"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              keyboardType={filter.value_type === 'number' ? 'numeric' : 'default'}
            />
          </View>
        )}
      </View>

      <TouchableOpacity
        style={styles.removeFilterButton}
        onPress={onRemove}
      >
        <Text style={styles.removeFilterButtonText}>✕</Text>
      </TouchableOpacity>
    </View>
  );
}

function getFilterRowStyles(theme) {
  return StyleSheet.create({
    filterRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 15,
      padding: 15,
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F9F9F9',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      width: '100%',
    },
    logicalGroupContainer: {
      marginRight: 10,
      alignItems: 'center',
    },
    logicalGroupLabel: {
      fontSize: 12,
      color: theme.colors.text,
      marginBottom: 5,
    },
    logicalGroupButton: {
      padding: 5,
      backgroundColor: theme.isDarkMode ? '#333' : '#E0E0E0',
      borderRadius: 4,
      minWidth: 50,
      alignItems: 'center',
    },
    logicalGroupButtonText: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.text,
    },
    filterFields: {
      flex: 1,
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
    },
    filterField: {
      width: Platform.OS === 'web' ? '30%' : '48%',
      minWidth: 140,
      marginRight: Platform.OS === 'web' ? 10 : 5,
      marginBottom: 15,
      position: 'relative',
      zIndex: 1,
    },
    filterFieldOpen: {
      zIndex: 10050,
    },
    filterTriggerShell: {
      width: '100%',
      backgroundColor: 'transparent',
    },
    filterLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 5,
      backgroundColor: 'transparent',
    },
    filterDropdownButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 4,
      padding: 10,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
      minHeight: 40,
    },
    filterDropdownButtonText: {
      fontSize: 14,
      color: theme.colors.text,
      flex: 1,
      backgroundColor: 'transparent',
    },
    dropdownArrow: {
      fontSize: 12,
      color: theme.colors.text,
      marginLeft: 8,
      backgroundColor: 'transparent',
    },
    filterDropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 5,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? '#555555' : '#CCCCCC',
      borderRadius: 4,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
      maxHeight: 220,
      minWidth: 180,
      zIndex: 100002,
      overflow: 'hidden',
      ...(Platform.OS === 'web' ? {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.45)',
      } : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 12,
      }),
    },
    filterDropdownOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 100001,
      backgroundColor: 'transparent',
    },
    filterDropdownScroll: {
      maxHeight: 200,
    },
    filterOption: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    filterOptionSelected: {
      backgroundColor: theme.isDarkMode ? '#FDFD0A' : '#0070D2',
    },
    filterOptionText: {
      fontSize: 14,
      color: theme.isDarkMode ? '#FFFFFF' : '#111111',
      backgroundColor: 'transparent',
    },
    filterOptionTextSelected: {
      color: theme.isDarkMode ? '#000000' : '#FFFFFF',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    filterValueInput: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 4,
      padding: 10,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
      color: theme.colors.text,
      fontSize: 14,
    },
    removeFilterButton: {
      width: 30,
      height: 30,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
      borderRadius: 4,
      marginLeft: 10,
    },
    removeFilterButtonText: {
      fontSize: 18,
      color: theme.isDarkMode ? '#FF4444' : '#CC0000',
      fontWeight: 'bold',
    },
  });
}
